
import React, { useState } from 'react';
import { X, UserPlus } from 'lucide-react';
import { Profile, UserRole } from '../../../types/user';

interface CreateUserData extends Omit<Profile, 'id' | 'created_at'> {
  password: string;
}

interface CreateUserModalProps {
  onClose: () => void;
  onCreate: (userData: CreateUserData) => Promise<void>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'user' as UserRole,
    is_active: true,
    calcom_url: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Name ist erforderlich';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    if (!formData.password) {
      newErrors.password = 'Passwort ist erforderlich';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      await onCreate({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
        calcom_url: formData.role === 'caller' ? formData.calcom_url.trim() || null : null,
        lead_stats: {
          total: 0,
          converted: 0,
          qualified: 0,
        },
        last_login: undefined,
      });

      // Only close if successful (onCreate will handle errors)
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Neuen Benutzer erstellen</h2>
            <p className="text-sm text-gray-500 mt-1">Fügen Sie einen neuen Benutzer zum System hinzu.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Vollständiger Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-1 ${
                errors.full_name
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Max Mustermann"
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-red-600">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              E-Mail-Adresse <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-1 ${
                errors.email
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="max@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Passwort <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-1 ${
                errors.password
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Mindestens 6 Zeichen"
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Rolle
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="user">Benutzer</option>
              <option value="kunde">Kunde</option>
              <option value="caller">Caller</option>
              <option value="admin">Administrator</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Administratoren haben vollen Zugriff. Kunde und Caller sehen nur
              Dashboard, Anfragen und Pipeline – Caller zusätzlich die Cal.com-Terminbuchung.
            </p>
          </div>

          {/* Cal.com-Link – nur relevant für Caller */}
          {formData.role === 'caller' && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Cal.com-Buchungslink
              </label>
              <input
                type="url"
                value={formData.calcom_url}
                onChange={(e) => setFormData({ ...formData, calcom_url: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="https://cal.com/webzen-studio/kurz-besprechung-ph"
              />
              <p className="mt-1 text-xs text-gray-500">
                Ziel des «Termin»-Buttons in der Anfrage-Detailansicht. Lead-Daten
                werden automatisch angehängt. Leer lassen = Standard-Link
                (Webzen Studio, Kurz-Besprechung).
              </p>
            </div>
          )}

          {/* Active Status */}
          <div className="flex items-start gap-3">
            <div className="flex h-5 items-center">
              <input
                id="create_is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="create_is_active" className="block text-sm font-bold text-gray-700 cursor-pointer">
                Konto ist aktiv
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Deaktivierte Benutzer können sich nicht anmelden.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="h-4 w-4" />
              {isLoading ? 'Wird erstellt...' : 'Benutzer erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
